import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import type { Currency } from '@/data/inventory/types'
import { productById } from '@/data/inventory/products'
import { setSuppliers } from '@/data/procurement/suppliers'
import type { POApprovalStatus, POLine, POStatus, PurchaseOrder, Supplier } from '@/data/procurement/types'

/* The mock dev tenant doubles as the demo legal entity (see SecurityConfig dev user). */
const LEGAL_ENTITY_ID = '00000000-0000-0000-0000-000000000001'

/* ───────────────────────────  GraphQL  ─────────────────────────── */

const GET_VENDORS = gql`
  query ProcVendors {
    vendors(pagination: { size: 200 }) {
      nodes {
        id
        vendorCode
        name
        displayName
        defaultCurrency
        status
        createdAt
        primaryContact {
          email
          phone
        }
        paymentTerms {
          netDays
        }
        billingAddress {
          city
          state
          country
        }
      }
    }
  }
`

const GET_PURCHASE_ORDERS = gql`
  query ProcPurchaseOrders {
    purchaseOrders(pagination: { size: 500 }) {
      nodes {
        id
        poNumber
        vendorId
        currency
        status
        approvalStatus
        orderDate
        expectedDeliveryDate
        orderPickupDate
        createdAt
        clearanceIssued
        clearanceIssuedByName
        clearanceIssuedAt
        amendments {
          id
          amendmentNumber
          reason
          changes
          amendedByName
          createdAt
        }
        subtotal {
          amount
        }
        taxAmount {
          amount
        }
        totalAmount {
          amount
        }
        lineItems {
          id
          productId
          description
          quantity
          unitPrice {
            amount
            currency
          }
          taxRate
        }
      }
    }
  }
`

export const RECEIVES_FOR_PO = gql`
  query ProcReceivesForPO($poId: UUID!) {
    receivesForPO(poId: $poId) {
      id
      receiveNumber
      warehouseId
      receivedDate
      status
      lineItems {
        id
        poLineItemId
        productId
        receivedQuantity
        acceptedQuantity
      }
    }
  }
`

const CREATE_VENDOR = gql`
  mutation ProcCreateVendor($input: VendorInput!) {
    createVendor(input: $input) {
      id
      vendorCode
      name
    }
  }
`

const CREATE_PO = gql`
  mutation ProcCreatePO($input: PurchaseOrderInput!) {
    createPurchaseOrder(input: $input) {
      id
      poNumber
      status
    }
  }
`

const AMEND_PO = gql`
  mutation ProcAmendPO($id: UUID!, $input: PurchaseOrderInput!, $reason: String!) {
    amendPurchaseOrder(id: $id, input: $input, reason: $reason) {
      id
      poNumber
      status
    }
  }
`

const SUBMIT_PO = gql`
  mutation ProcSubmitPO($id: UUID!) {
    submitPOForApproval(id: $id) {
      id
      poNumber
      status
    }
  }
`

const RAISE_ACTION_REQUEST = gql`
  mutation ProcRaiseActionRequest($input: RaiseActionRequestInput!) {
    raiseActionRequest(input: $input) {
      id
    }
  }
`

const APPROVE_PO = gql`
  mutation ProcApprovePO($id: UUID!) {
    approvePurchaseOrder(id: $id) {
      id
      status
    }
  }
`

const SEND_PO = gql`
  mutation ProcSendPO($id: UUID!) {
    sendPOToVendor(id: $id) {
      id
      status
    }
  }
`

const CANCEL_PO = gql`
  mutation ProcCancelPO($id: UUID!) {
    cancelPurchaseOrder(id: $id) {
      id
      status
    }
  }
`

const CREATE_PURCHASE_RECEIVE = gql`
  mutation ProcCreateReceive($input: PurchaseReceiveInput!) {
    createPurchaseReceive(input: $input) {
      id
      receiveNumber
      status
    }
  }
`

const COMPLETE_RECEIVE = gql`
  mutation ProcCompleteReceive($id: UUID!) {
    completeReceive(id: $id) {
      id
      status
    }
  }
`

const ISSUE_CLEARANCE = gql`
  mutation ProcIssueClearance($poId: UUID!, $signatoryName: String) {
    issueClearanceCertificate(poId: $poId, signatoryName: $signatoryName) {
      id
      clearanceIssued
      clearanceIssuedByName
      clearanceIssuedAt
    }
  }
`

/* ───────────────────────────  Raw GQL shapes  ─────────────────────────── */

interface GqlMoney {
  amount: string
  currency: string
}

interface GqlVendor {
  id: string
  vendorCode: string
  name: string
  displayName?: string | null
  defaultCurrency: string
  status: 'ACTIVE' | 'INACTIVE' | 'BLOCKED'
  createdAt: string
  primaryContact?: { email?: string | null; phone?: string | null } | null
  paymentTerms?: { netDays: number } | null
  billingAddress?: { city?: string | null; state?: string | null; country?: string | null } | null
}

interface GqlPOLine {
  id: string
  productId?: string | null
  description: string
  quantity: string
  unitPrice: GqlMoney
  taxRate?: string | null
}

type GqlPOStatus =
  | 'DRAFT'
  | 'PENDING_APPROVAL'
  | 'APPROVED'
  | 'SENT_TO_VENDOR'
  | 'PARTIALLY_RECEIVED'
  | 'FULLY_RECEIVED'
  | 'CANCELLED'
  | 'CLOSED'

type GqlApprovalStatus = 'NOT_REQUIRED' | 'PENDING' | 'APPROVED' | 'REJECTED'

interface GqlPurchaseOrder {
  id: string
  poNumber: string
  vendorId: string
  currency: string
  status: GqlPOStatus
  approvalStatus?: GqlApprovalStatus | null
  orderDate: string
  expectedDeliveryDate?: string | null
  orderPickupDate?: string | null
  createdAt: string
  clearanceIssued?: boolean | null
  clearanceIssuedByName?: string | null
  clearanceIssuedAt?: string | null
  subtotal?: { amount: string } | null
  taxAmount?: { amount: string } | null
  totalAmount?: { amount: string } | null
  lineItems: GqlPOLine[]
  amendments?: GqlPOAmendment[] | null
}

interface GqlPOAmendment {
  id: string
  amendmentNumber: number
  reason?: string | null
  changes?: string | null
  amendedByName?: string | null
  createdAt?: string | null
}

/* ───────────────────────────  Mappers  ─────────────────────────── */

const STATUS_MAP: Record<GqlPOStatus, POStatus> = {
  DRAFT: 'draft',
  PENDING_APPROVAL: 'submitted',
  APPROVED: 'confirmed',
  SENT_TO_VENDOR: 'confirmed',
  PARTIALLY_RECEIVED: 'partially_received',
  FULLY_RECEIVED: 'received',
  CLOSED: 'received',
  CANCELLED: 'cancelled',
}

const APPROVAL_MAP: Record<GqlApprovalStatus, POApprovalStatus> = {
  NOT_REQUIRED: 'not_required',
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
}

function mapPO(po: GqlPurchaseOrder): PurchaseOrder {
  return {
    id: po.id,
    poNumber: po.poNumber,
    supplierId: po.vendorId,
    currency: po.currency,
    status: STATUS_MAP[po.status] ?? 'draft',
    approvalStatus: po.approvalStatus ? APPROVAL_MAP[po.approvalStatus] : undefined,
    expectedDelivery: po.expectedDeliveryDate ?? null,
    orderPickupDate: po.orderPickupDate ?? null,
    amendments: (po.amendments ?? []).map((a) => ({
      id: a.id,
      amendmentNumber: a.amendmentNumber,
      reason: a.reason ?? null,
      changes: a.changes ?? null,
      amendedByName: a.amendedByName ?? null,
      createdAt: a.createdAt ?? null,
    })),
    lines: (po.lineItems ?? []).map((l) => ({
      poLineItemId: l.id,
      productId: l.productId ?? '',
      description: l.description ?? '',
      quantity: Number(l.quantity) || 0,
      unitCost: Number(l.unitPrice?.amount) || 0,
      receivedQuantity: 0,
    })),
    subtotal: Number(po.subtotal?.amount) || 0,
    taxAmount: Number(po.taxAmount?.amount) || 0,
    totalAmount: Number(po.totalAmount?.amount) || 0,
    createdBy: '',
    createdAt: po.createdAt ?? po.orderDate,
    notes: undefined,
    sourceAlertId: null,
    paymentStatus: 'unpaid',
    clearanceIssued: po.clearanceIssued ?? false,
    clearanceIssuedByName: po.clearanceIssuedByName ?? null,
    clearanceIssuedAt: po.clearanceIssuedAt ?? null,
  }
}

function buildSuppliers(vendors: GqlVendor[], pos: PurchaseOrder[]): Supplier[] {
  // Derive product coverage + last-order date from PO history (backend vendors have no product list).
  const productCoverage = new Map<string, Set<string>>()
  const lastOrder = new Map<string, string>()
  for (const po of pos) {
    const set = productCoverage.get(po.supplierId) ?? new Set<string>()
    po.lines.forEach((l) => l.productId && set.add(l.productId))
    productCoverage.set(po.supplierId, set)
    const prev = lastOrder.get(po.supplierId)
    if (!prev || new Date(po.createdAt) > new Date(prev)) lastOrder.set(po.supplierId, po.createdAt)
  }

  return vendors.map((v) => {
    const city = [v.billingAddress?.city, v.billingAddress?.country]
      .filter(Boolean)
      .join(', ')
    return {
      id: v.id,
      code: v.vendorCode,
      name: v.name,
      productIds: [...(productCoverage.get(v.id) ?? new Set<string>())],
      leadTimeDays: 0,
      currency: v.defaultCurrency,
      paymentTerms: v.paymentTerms ? `Net ${v.paymentTerms.netDays}` : undefined,
      email: v.primaryContact?.email ?? undefined,
      phone: v.primaryContact?.phone ?? undefined,
      city: city || undefined,
      status: v.status === 'ACTIVE' ? 'active' : v.status === 'BLOCKED' ? 'blocked' : 'inactive',
      lastOrderAt: lastOrder.get(v.id),
    }
  })
}

/* ───────────────────────────  Receives (per-PO) hook  ─────────────────────────── */

export interface POReceiveLine {
  productId: string
  poLineItemId: string
  received: number
}

export interface POReceive {
  id: string
  receiveNumber: string
  warehouseId: string
  receivedDate: string
  status: 'draft' | 'completed' | 'cancelled'
  lines: POReceiveLine[]
}

interface GqlReceive {
  id: string
  receiveNumber: string
  warehouseId: string
  receivedDate: string
  status: 'DRAFT' | 'COMPLETED' | 'CANCELLED'
  lineItems: {
    id: string
    poLineItemId: string
    productId?: string | null
    receivedQuantity: string
    acceptedQuantity?: string | null
  }[]
}

/** Live goods-receipts for a single PO + the received-per-product roll-up (accepted qty). */
export function usePOReceives(poId: string | undefined) {
  const { data } = useQuery(RECEIVES_FOR_PO, {
    variables: { poId },
    skip: !poId,
  })
  return useMemo(() => {
    const raw: GqlReceive[] = data?.receivesForPO ?? []
    const receives: POReceive[] = raw.map((r) => ({
      id: r.id,
      receiveNumber: r.receiveNumber,
      warehouseId: r.warehouseId,
      receivedDate: r.receivedDate,
      status: r.status.toLowerCase() as POReceive['status'],
      lines: (r.lineItems ?? []).map((l) => ({
        productId: l.productId ?? '',
        poLineItemId: l.poLineItemId,
        received: Number(l.acceptedQuantity ?? l.receivedQuantity) || 0,
      })),
    }))
    const receivedByProduct = new Map<string, number>()
    for (const r of receives) {
      if (r.status === 'cancelled') continue
      for (const l of r.lines) receivedByProduct.set(l.productId, (receivedByProduct.get(l.productId) ?? 0) + l.received)
    }
    return { receives, receivedByProduct }
  }, [data])
}

/* ───────────────────────────  Context  ─────────────────────────── */

interface CreatePOInput {
  supplierId: string
  currency: Currency
  expectedDelivery: string | null
  orderPickupDate?: string | null
  notes?: string
  lines: POLine[]
  sourceAlertId?: string | null
  submit?: boolean
}

export interface AmendPOInput {
  expectedDelivery?: string | null
  orderPickupDate?: string | null
  lines: POLine[]
  reason: string
}

export interface NewSupplierInput {
  name: string
  displayName?: string
  currency?: string
  tier?: string
  status?: string
  netDays?: number
  taxIdType?: string
  taxIdValue?: string
  contactName?: string
  contactEmail?: string
  contactPhone?: string
}

export interface ReceiveAgainstPOInput {
  poId: string
  warehouseId: string
  receivedDate: string
  lines: {
    poLineItemId: string
    productId: string
    receivedQuantity: number
    lotNumber?: string
    expiryDate?: string | null
  }[]
}

interface ProcurementContextValue {
  suppliers: Supplier[]
  purchaseOrders: PurchaseOrder[]
  loading: boolean
  createPO: (input: CreatePOInput) => Promise<PurchaseOrder>
  /** Amend an approved/sent PO (line items, prices, pickup/delivery dates) with a reason; records amendment history. */
  amendPO: (id: string, input: AmendPOInput) => Promise<void>
  setPOStatus: (id: string, status: POStatus) => Promise<void>
  createSupplier: (input: NewSupplierInput) => Promise<void>
  /** Receive goods against a PO — creates + completes a PurchaseReceive, flipping the PO to (PARTIALLY_)RECEIVED and creating stock lots. */
  receiveAgainstPO: (input: ReceiveAgainstPOInput) => Promise<void>
  /** Issue the certificate of clearance for a PO (procurement sign-off that gates receiving). */
  issueClearance: (poId: string, signatoryName?: string) => Promise<void>
}

const ProcurementContext = createContext<ProcurementContextValue | null>(null)

export function ProcurementProvider({ children }: { children: ReactNode }) {
  const vendorsQ = useQuery(GET_VENDORS)
  const poQ = useQuery(GET_PURCHASE_ORDERS)

  const [createPurchaseOrder] = useMutation(CREATE_PO)
  const [amendPurchaseOrder] = useMutation(AMEND_PO)
  const [createVendor] = useMutation(CREATE_VENDOR)
  const [raiseActionRequest] = useMutation(RAISE_ACTION_REQUEST)
  const [submitPO] = useMutation(SUBMIT_PO)
  const [approvePO] = useMutation(APPROVE_PO)
  const [sendPO] = useMutation(SEND_PO)
  const [cancelPO] = useMutation(CANCEL_PO)
  const [createPurchaseReceive] = useMutation(CREATE_PURCHASE_RECEIVE)
  const [completeReceive] = useMutation(COMPLETE_RECEIVE)
  const [issueClearanceMut] = useMutation(ISSUE_CLEARANCE)

  const rawVendors: GqlVendor[] = vendorsQ.data?.vendors?.nodes ?? []
  const rawPOs: GqlPurchaseOrder[] = poQ.data?.purchaseOrders?.nodes ?? []

  const purchaseOrders = useMemo(() => rawPOs.map(mapPO), [rawPOs])

  const suppliers = useMemo(() => {
    const built = buildSuppliers(rawVendors, purchaseOrders)
    setSuppliers(built)
    return built
  }, [rawVendors, purchaseOrders])

  const refetchAll = useCallback(async () => {
    await Promise.all([poQ.refetch(), vendorsQ.refetch()])
  }, [poQ, vendorsQ])

  // When a submitted PO lands in PENDING_APPROVAL, notify the procurement managers to approve it.
  const notifyApprovalIfPending = useCallback(
    async (submitResult: { data?: { submitPOForApproval?: { id: string; poNumber?: string; status?: string } } } | undefined) => {
      const po = submitResult?.data?.submitPOForApproval
      if (!po || po.status !== 'PENDING_APPROVAL') return
      await raiseActionRequest({
        variables: {
          input: {
            actionType: 'APPROVE_PURCHASE_ORDER',
            targetRole: 'PROCUREMENT_MANAGER',
            title: `PO ${po.poNumber ?? ''} needs approval`.trim(),
            message: 'Submitted for approval — review and confirm to send it to the vendor.',
            context: JSON.stringify({ route: `/procurement/orders/${po.id}`, poId: po.id }),
          },
        },
      }).catch(() => undefined)
    },
    [raiseActionRequest],
  )

  const createPO = useCallback<ProcurementContextValue['createPO']>(
    async (input) => {
      const lineItems = input.lines
        .filter((l) => l.productId)
        .map((l) => {
          const p = productById(l.productId)
          return {
            productId: l.productId,
            description: p?.name ?? 'Item',
            quantity: l.quantity,
            uomCode: p?.baseUnit ?? 'EA',
            uomName: p?.units?.[0]?.label ?? p?.baseUnit ?? 'Each',
            unitPrice: l.unitCost,
          }
        })

      const today = new Date().toISOString().slice(0, 10)
      // The Apollo client uses errorPolicy: 'all', so GraphQL errors do NOT reject the
      // promise — they arrive in `errors` with `data: null`. Surface them explicitly so
      // a failed create/submit is never mistaken for success by the caller.
      const { data, errors } = await createPurchaseOrder({
        variables: {
          input: {
            vendorId: input.supplierId,
            legalEntityId: LEGAL_ENTITY_ID,
            orderDate: today,
            expectedDeliveryDate: input.expectedDelivery,
            orderPickupDate: input.orderPickupDate ?? null,
            currency: input.currency,
            lineItems,
          },
        },
      })
      if (errors?.length) throw new Error(errors[0].message)

      const created = data?.createPurchaseOrder
      if (!created?.id) throw new Error('Purchase order could not be created')

      if (input.submit) {
        const submitRes = await submitPO({ variables: { id: created.id } })
        if (submitRes.errors?.length) throw new Error(submitRes.errors[0].message)
        await notifyApprovalIfPending(submitRes)
      }
      await refetchAll()

      return {
        id: created?.id ?? '',
        poNumber: created?.poNumber ?? '',
        supplierId: input.supplierId,
        currency: input.currency,
        status: input.submit ? 'submitted' : 'draft',
        expectedDelivery: input.expectedDelivery,
        orderPickupDate: input.orderPickupDate ?? null,
        lines: input.lines.map((l) => ({ ...l, receivedQuantity: 0 })),
        createdBy: '',
        createdAt: new Date().toISOString(),
        notes: input.notes,
        sourceAlertId: input.sourceAlertId ?? null,
        paymentStatus: 'unpaid',
      }
    },
    [createPurchaseOrder, submitPO, refetchAll, notifyApprovalIfPending],
  )

  const amendPO = useCallback<ProcurementContextValue['amendPO']>(
    async (id, input) => {
      const lineItems = input.lines
        .filter((l) => l.productId)
        .map((l) => {
          const p = productById(l.productId)
          return {
            productId: l.productId,
            description: l.description?.trim() || p?.name || 'Item',
            quantity: l.quantity,
            uomCode: p?.baseUnit ?? 'EA',
            uomName: p?.units?.[0]?.label ?? p?.baseUnit ?? 'Each',
            unitPrice: l.unitCost,
          }
        })
      const today = new Date().toISOString().slice(0, 10)
      const { errors } = await amendPurchaseOrder({
        variables: {
          id,
          reason: input.reason,
          input: {
            vendorId: '00000000-0000-0000-0000-000000000000',
            legalEntityId: LEGAL_ENTITY_ID,
            orderDate: today,
            expectedDeliveryDate: input.expectedDelivery ?? null,
            orderPickupDate: input.orderPickupDate ?? null,
            currency: 'INR',
            lineItems,
          },
        },
      })
      if (errors?.length) throw new Error(errors[0].message)
      await refetchAll()
    },
    [amendPurchaseOrder, refetchAll],
  )

  const setPOStatus = useCallback<ProcurementContextValue['setPOStatus']>(
    async (id, status) => {
      if (status === 'submitted') {
        const submitRes = await submitPO({ variables: { id } })
        await notifyApprovalIfPending(submitRes)
      } else if (status === 'confirmed') {
        await approvePO({ variables: { id } })
        await sendPO({ variables: { id } }).catch(() => undefined)
      } else if (status === 'cancelled') {
        await cancelPO({ variables: { id } })
      }
      await refetchAll()
    },
    [submitPO, approvePO, sendPO, cancelPO, refetchAll, notifyApprovalIfPending],
  )

  const receiveAgainstPO = useCallback<ProcurementContextValue['receiveAgainstPO']>(
    async (input) => {
      const { data, errors } = await createPurchaseReceive({
        variables: {
          input: {
            purchaseOrderId: input.poId,
            warehouseId: input.warehouseId,
            receivedDate: input.receivedDate || undefined,
            lineItems: input.lines
              .filter((l) => l.receivedQuantity > 0)
              .map((l) => ({
                poLineItemId: l.poLineItemId,
                productId: l.productId || undefined,
                receivedQuantity: String(l.receivedQuantity),
                acceptedQuantity: String(l.receivedQuantity),
                lotNumber: l.lotNumber?.trim() || undefined,
                expiryDate: l.expiryDate || undefined,
                inspectionStatus: 'PASSED',
              })),
          },
        },
      })
      if (errors?.length) throw new Error(errors[0].message)
      const receiveId = data?.createPurchaseReceive?.id
      if (!receiveId) throw new Error('Goods receipt could not be created')

      // Completing the receipt posts stock (GoodsReceivedEvent → lots) and advances the PO's received qty.
      const completeRes = await completeReceive({ variables: { id: receiveId } })
      if (completeRes.errors?.length) throw new Error(completeRes.errors[0].message)
      await refetchAll()
    },
    [createPurchaseReceive, completeReceive, refetchAll],
  )

  const issueClearance = useCallback<ProcurementContextValue['issueClearance']>(
    async (poId, signatoryName) => {
      const { errors } = await issueClearanceMut({
        variables: { poId, signatoryName: signatoryName?.trim() || null },
      })
      if (errors?.length) throw new Error(errors[0].message)
      await refetchAll()
    },
    [issueClearanceMut, refetchAll],
  )

  const createSupplier = useCallback<ProcurementContextValue['createSupplier']>(
    async (input) => {
      const netDays = input.netDays
      await createVendor({
        variables: {
          input: {
            name: input.name.trim(),
            displayName: input.displayName?.trim() || undefined,
            defaultCurrency: input.currency || undefined,
            tier: input.tier || undefined,
            status: input.status || undefined,
            paymentTerms: netDays != null && !Number.isNaN(netDays) ? { netDays } : undefined,
            // Tax identifier is optional. Only send the pair when a value is provided; the backend
            // applies it only when both type + value exist.
            taxIdentifierType: input.taxIdValue?.trim() ? input.taxIdType || 'TIN' : undefined,
            taxIdentifierValue: input.taxIdValue?.trim() || undefined,
            contacts: input.contactName?.trim()
              ? [
                  {
                    name: input.contactName.trim(),
                    email: input.contactEmail?.trim() || undefined,
                    phone: input.contactPhone?.trim() || undefined,
                    primary: true,
                  },
                ]
              : undefined,
          },
        },
      })
      await refetchAll()
    },
    [createVendor, refetchAll],
  )

  const value = useMemo<ProcurementContextValue>(
    () => ({
      suppliers,
      purchaseOrders,
      loading: vendorsQ.loading || poQ.loading,
      createPO,
      amendPO,
      setPOStatus,
      createSupplier,
      receiveAgainstPO,
      issueClearance,
    }),
    [suppliers, purchaseOrders, vendorsQ.loading, poQ.loading, createPO, amendPO, setPOStatus, createSupplier, receiveAgainstPO, issueClearance],
  )

  return <ProcurementContext.Provider value={value}>{children}</ProcurementContext.Provider>
}

export function useProcurement(): ProcurementContextValue {
  const ctx = useContext(ProcurementContext)
  if (!ctx) throw new Error('useProcurement must be used within ProcurementProvider')
  return ctx
}
